/**
 * HR: what a person costs an employer in Saudi Arabia, and what that makes an
 * hour of their work cost.
 *
 * This mirrors `backend/api/slt/hr/saudi.py` line for line — the platform
 * computes with that, the demonstration with this. Change both.
 *
 * The rules are data: every rate is a default the airport can restate on the
 * rules screen, and each default names the article or scheme it comes from.
 * They were written from the published law as understood on 2026-09-26 and
 * are *proposed* until HR or legal confirms them against GOSI, Qiwa and the
 * Ministry. The ticket and the medical premium are contract and policy
 * figures, not law, and are labelled so.
 */

export const TRADES = [
  "electrical",
  "civil",
  "mechanical",
  "conveyance",
  "systems",
  "safety",
  "operations",
  "administration",
] as const;
export type Trade = (typeof TRADES)[number];

export const TRADE_LABEL: Record<Trade, string> = {
  electrical: "Electrical",
  civil: "Civil",
  mechanical: "Mechanical",
  conveyance: "Conveyance",
  systems: "Systems (ICT, security, screening)",
  safety: "Fire and life safety",
  operations: "Operations",
  administration: "Administration",
};

export type Employment = "direct" | "outsourced";
export type ContractType = "indefinite" | "fixed";
export type EmployeeStatus = "active" | "on_leave" | "left";
export type LeaveReason = "termination" | "resignation" | "contract_end";

export const STATUS_LABEL: Record<EmployeeStatus, string> = {
  active: "Active",
  on_leave: "On leave",
  left: "Left",
};

/* Nationalities offered in the form; any ISO code is accepted by the platform. */
export const NATIONALITIES: { code: string; name: string }[] = [
  { code: "SA", name: "Saudi Arabia" },
  { code: "EG", name: "Egypt" },
  { code: "IN", name: "India" },
  { code: "PK", name: "Pakistan" },
  { code: "BD", name: "Bangladesh" },
  { code: "PH", name: "Philippines" },
  { code: "JO", name: "Jordan" },
  { code: "SD", name: "Sudan" },
  { code: "YE", name: "Yemen" },
  { code: "NP", name: "Nepal" },
  { code: "LK", name: "Sri Lanka" },
  { code: "GB", name: "United Kingdom" },
];

export interface HrEmployee {
  id: string;
  siteId: string | null;
  employeeNo: string;
  name: string;
  nationality: string;
  saudi: boolean;
  department: string | null;
  trade: Trade | null;
  jobTitle: string | null;
  grade: string | null;
  employment: Employment;
  contractType: ContractType;
  hiredOn: string;
  contractEnd: string | null;
  leftOn: string | null;
  status: EmployeeStatus;
  basic: number;
  housing: number;
  transport: number;
  other: number;
  outsourcedMonthly: number | null;
  medicalAnnual: number | null;
  gosiNewEntrant: boolean | null;
  weeklyHours: number;
  costCentre: string | null;
  principalId: string | null;
  principalName: string | null;
  notes: string | null;
  serviceYears: number;
  gosiRegime: "new entrant" | "legacy" | null;
  gosiRegimeSource: "recorded" | "assumed from hire date" | null;
  /* the staff file's personal details (migration 0065); all optional */
  dateOfBirth?: string | null;
  gender?: "male" | "female" | null;
  maritalStatus?: "single" | "married" | "divorced" | "widowed" | null;
  idNumber?: string | null;
  idExpiry?: string | null;
  passportNo?: string | null;
  passportExpiry?: string | null;
  email?: string | null;
  phone?: string | null;
  address?: string | null;
  emergencyContact?: { name?: string; relation?: string; phone?: string } | null;
  hasPhoto?: boolean;
  /** how HR has set this person to check in; null = not decided (readers only) */
  clockMethods?: ("phone_location" | "terminal_face" | "terminal_fingerprint")[] | null;
  /** the locations they may check in at by phone; null = any */
  clockGeofenceIds?: string[] | null;
  clockBasis?: string | null;
  clockSetAt?: string | null;
  /** true when the reader has no hr:pay and the platform left the pay out */
  payHidden?: boolean;
}

export type HrEmployeeInput = Omit<
  HrEmployee,
  "id" | "saudi" | "principalName" | "serviceYears" | "gosiRegime" | "gosiRegimeSource" | "hasPhoto" | "payHidden"
>;

export type HrEmployeePatch = Partial<HrEmployeeInput>;

export interface HrUser {
  id: string;
  displayName: string;
  email: string | null;
}

export interface HrPeople {
  /** false when the reader has no hr:pay: every pay field is then null */
  canPay?: boolean;
  employees: HrEmployee[];
  users: HrUser[];
  departments: string[];
  trades: Trade[];
  rulesBasis: "default" | "entered";
}

export interface ScheduleStep {
  from: string;
  employer: number;
  employee: number;
}

export interface HrRules {
  asOf: string;
  currency: string;
  basis: "default" | "entered";
  gosi: {
    legacyAnnuityEmployer: number;
    legacyAnnuityEmployee: number;
    newEntrantSince: string;
    newEntrantSchedule: ScheduleStep[];
    sanedEmployer: number;
    sanedEmployee: number;
    hazardsEmployer: number;
    baseMin: number;
    baseMax: number;
  };
  eosb: {
    firstYears: number;
    firstRateMonths: number;
    laterRateMonths: number;
    wage: "actual" | "basic_housing";
    resignation: { belowYears: number; factor: number }[];
  };
  leave: { days: number; daysAfter: number; afterYears: number };
  hours: {
    weeklyMax: number;
    ramadanWeekly: number;
    workdaysPerWeek: number;
    publicHolidayDays: number;
    sickAllowanceDays: number;
  };
  overtime: { premiumOfBasic: number };
  expat: {
    levyMonthly: number;
    iqamaAnnual: number;
    workPermitAnnual: number;
    exitReentryAnnual: number;
    ticketAnnual: number;
  };
  medicalAnnualDefault: number;
  nitaqat: { fullAtWage: number; halfAtWage: number | null; targetPct: number | null };
  /** asset category → trade; the generic defaults plus what the tenant added */
  tradeByCategory: Record<string, string>;
}

/** What restating a rule looks like: any part of the rules, nested. */
export type HrRulesPatch = {
  [K in keyof Omit<HrRules, "asOf" | "currency" | "basis">]?: Partial<HrRules[K]> | HrRules[K];
};

export interface HrRulesView {
  rules: HrRules;
  defaults: HrRules;
  restated: HrRulesPatch;
  updatedAt: string | null;
  updatedBy: string | null;
}

export const RULES_AS_OF = "2026-09-26";

export function defaultRules(): HrRules {
  return {
    asOf: RULES_AS_OF,
    currency: "SAR",
    basis: "default",
    gosi: {
      legacyAnnuityEmployer: 9,
      legacyAnnuityEmployee: 9,
      newEntrantSince: "2024-07-03",
      newEntrantSchedule: [
        { from: "2024-07-03", employer: 9, employee: 9 },
        { from: "2025-07-01", employer: 9.5, employee: 9.5 },
        { from: "2026-07-01", employer: 10, employee: 10 },
        { from: "2027-07-01", employer: 10.5, employee: 10.5 },
        { from: "2028-07-01", employer: 11, employee: 11 },
      ],
      sanedEmployer: 0.75,
      sanedEmployee: 0.75,
      hazardsEmployer: 2,
      baseMin: 1500,
      baseMax: 45000,
    },
    eosb: {
      firstYears: 5,
      firstRateMonths: 0.5,
      laterRateMonths: 1,
      wage: "actual",
      resignation: [
        { belowYears: 2, factor: 0 },
        { belowYears: 5, factor: 1 / 3 },
        { belowYears: 10, factor: 2 / 3 },
      ],
    },
    leave: { days: 21, daysAfter: 30, afterYears: 5 },
    hours: { weeklyMax: 48, ramadanWeekly: 36, workdaysPerWeek: 6, publicHolidayDays: 10, sickAllowanceDays: 5 },
    overtime: { premiumOfBasic: 0.5 },
    expat: { levyMonthly: 800, iqamaAnnual: 650, workPermitAnnual: 100, exitReentryAnnual: 200, ticketAnnual: 2500 },
    medicalAnnualDefault: 4500,
    nitaqat: { fullAtWage: 4000, halfAtWage: 3000, targetPct: null },
    tradeByCategory: { ...DEFAULT_TRADE_BY_CATEGORY },
  };
}

/**
 * Where each rule comes from, for the rules screen. `law` is a statute or a
 * scheme's published rate; `practice` is a planning figure or a contract term.
 */
export const RULE_SOURCES: { key: string; rule: string; source: string; kind: "law" | "practice" }[] = [
  {
    key: "gosi",
    rule: "GOSI contributions on basic + housing, floored at SAR 1,500 and capped at SAR 45,000",
    source: "Social Insurance Law (Royal Decree M/273), in force 3 July 2024",
    kind: "law",
  },
  {
    key: "gosi.new",
    rule: "New entrants' annuity rate rises half a point a side each July to 11%",
    source: "Social Insurance Law, transitional schedule for those first registered from 3 July 2024",
    kind: "law",
  },
  {
    key: "saned",
    rule: "SANED unemployment insurance, 0.75% employer and 0.75% employee, Saudis only",
    source: "Unemployment Insurance (SANED) Law",
    kind: "law",
  },
  {
    key: "hazards",
    rule: "Occupational hazards, 2% employer, every employee",
    source: "Social Insurance Law, occupational hazards branch",
    kind: "law",
  },
  {
    key: "eosb",
    rule: "End of service: half a month's wage a year for the first five years, a month's wage a year after",
    source: "Labour Law, article 84",
    kind: "law",
  },
  {
    key: "eosb.resign",
    rule: "On resignation: nothing under 2 years, a third from 2 to 5, two thirds from 5 to 10, all after 10",
    source: "Labour Law, article 85",
    kind: "law",
  },
  {
    key: "leave",
    rule: "Annual leave of 21 days, 30 after five years with the employer",
    source: "Labour Law, article 109",
    kind: "law",
  },
  {
    key: "hours",
    rule: "48 hours a week, 8 a day; 36 a week in Ramadan for Muslim workers",
    source: "Labour Law, article 98",
    kind: "law",
  },
  {
    key: "overtime",
    rule: "Overtime: the hour's wage plus 50% of the hour's basic wage",
    source: "Labour Law, article 107",
    kind: "law",
  },
  {
    key: "holidays",
    rule: "Public holidays: Eid al-Fitr 4 days, Eid al-Adha 4 days, Founding Day, National Day",
    source: "Implementing Regulations of the Labour Law",
    kind: "law",
  },
  {
    key: "levy",
    rule: "Work-permit levy on each non-Saudi employee, monthly",
    source: "Expat levy (al-muqabil al-mali), Council of Ministers decision",
    kind: "law",
  },
  {
    key: "iqama",
    rule: "Iqama, work permit and exit/re-entry fees, yearly",
    source: "Jawazat and Ministry fee schedules",
    kind: "law",
  },
  {
    key: "medical",
    rule: "Medical cover for every employee (the premium is the policy's)",
    source: "Cooperative Health Insurance Law (CHI)",
    kind: "law",
  },
  {
    key: "nitaqat",
    rule: "A Saudi counts in full at SAR 4,000 and half at SAR 3,000 (basic + housing registered with GOSI)",
    source: "Nitaqat programme (HRSD / Qiwa); the band depends on the activity and size",
    kind: "law",
  },
  {
    key: "ticket",
    rule: "An annual return ticket for non-Saudi staff",
    source: "Employment contract, not the law",
    kind: "practice",
  },
  {
    key: "sick",
    rule: "Five sick days a year allowed for in the hourly rate",
    source: "A planning allowance (art. 117 sets sick pay, not an expected number)",
    kind: "practice",
  },
];

/* ---- rules, merged ------------------------------------------------------------ */

type Plain = Record<string, unknown>;

export function mergeRules(restated: HrRulesPatch | null | undefined): HrRules {
  const out = defaultRules() as unknown as Plain;
  if (!restated || !Object.keys(restated).length) return out as unknown as HrRules;
  const lay = (dst: Plain, src: Plain) => {
    for (const [k, v] of Object.entries(src)) {
      if (
        v &&
        typeof v === "object" &&
        !Array.isArray(v) &&
        dst[k] &&
        typeof dst[k] === "object" &&
        !Array.isArray(dst[k])
      )
        lay(dst[k] as Plain, v as Plain);
      else dst[k] = v;
    }
  };
  lay(out, restated as Plain);
  out.basis = "entered";
  return out as unknown as HrRules;
}

/* ---- the arithmetic ----------------------------------------------------------- */

const DAY = 86_400_000;
const r2 = (v: number) => Math.round(v * 100) / 100;
const r1 = (v: number) => Math.round(v * 10) / 10;
const date = (s: string) => new Date(`${s.slice(0, 10)}T00:00:00Z`);

/**
 * An asset's category to the trade that works on it. Only generic engineering
 * words are defaults; a pack's own categories ("airfield lighting",
 * "screening") are mapped in the tenant's rules (`tradeByCategory`), as on
 * the platform, whose core must not learn a domain's vocabulary.
 */
export const DEFAULT_TRADE_BY_CATEGORY: Record<string, Trade> = {
  electrical: "electrical",
  lighting: "electrical",
  power: "electrical",
  civil: "civil",
  pavement: "civil",
  building: "civil",
  mechanical: "mechanical",
  hvac: "mechanical",
  plumbing: "mechanical",
  conveyance: "conveyance",
  security: "systems",
  information: "systems",
  ict: "systems",
  safety: "safety",
  fire: "safety",
};

export function tradeOfCategory(category: string | null | undefined, rules?: Pick<HrRules, "tradeByCategory"> | null): Trade | null {
  const table = rules?.tradeByCategory ?? DEFAULT_TRADE_BY_CATEGORY;
  return (table[(category ?? "").trim().toLowerCase()] as Trade | undefined) ?? null;
}

export function serviceYears(hiredOn: string | null | undefined, on: Date): number {
  if (!hiredOn) return 0;
  const h = date(hiredOn);
  if (h > on) return 0;
  return Math.round((on.getTime() - h.getTime()) / DAY) / 365.25;
}

export const isSaudi = (e: Pick<HrEmployee, "nationality">) => (e.nationality ?? "").toUpperCase() === "SA";

export const grossPay = (e: Pick<HrEmployee, "basic" | "housing" | "transport" | "other">) =>
  (e.basic || 0) + (e.housing || 0) + (e.transport || 0) + (e.other || 0);

export function newEntrant(
  e: Pick<HrEmployee, "gosiNewEntrant" | "hiredOn">,
  rules: HrRules,
): { isNew: boolean; source: "recorded" | "assumed from hire date" } {
  if (e.gosiNewEntrant != null) return { isNew: e.gosiNewEntrant, source: "recorded" };
  return {
    isNew: !!e.hiredOn && e.hiredOn.slice(0, 10) >= rules.gosi.newEntrantSince,
    source: "assumed from hire date",
  };
}

export function annuityRates(rules: HrRules, isNew: boolean, on: Date): [number, number] {
  const g = rules.gosi;
  if (!isNew) return [g.legacyAnnuityEmployer, g.legacyAnnuityEmployee];
  const today = on.toISOString().slice(0, 10);
  const steps = [...g.newEntrantSchedule].sort((a, b) => a.from.localeCompare(b.from)).filter((s) => s.from <= today);
  const step = steps[steps.length - 1];
  return step ? [step.employer, step.employee] : [g.legacyAnnuityEmployer, g.legacyAnnuityEmployee];
}

export function resignationFactor(years: number, rules: HrRules): number {
  for (const s of [...rules.eosb.resignation].sort((a, b) => a.belowYears - b.belowYears))
    if (years < s.belowYears) return s.factor;
  return 1;
}

export interface EosbResult {
  wage: number;
  years: number;
  reason: LeaveReason;
  firstYears: number;
  firstAmount: number;
  laterYears: number;
  laterAmount: number;
  full: number;
  factor: number;
  amount: number;
}

/** The award on leaving, art. 84, cut for a resignation by art. 85. */
export function eosb(wage: number, years: number, reason: LeaveReason, rules: HrRules): EosbResult {
  const r = rules.eosb;
  const first = Math.min(years, r.firstYears);
  const later = Math.max(0, years - r.firstYears);
  const firstAmount = wage * r.firstRateMonths * first;
  const laterAmount = wage * r.laterRateMonths * later;
  const full = firstAmount + laterAmount;
  const factor = reason === "resignation" ? resignationFactor(years, rules) : 1;
  return {
    wage: r2(wage),
    years: Math.round(years * 1000) / 1000,
    reason,
    firstYears: Math.round(first * 1000) / 1000,
    firstAmount: r2(firstAmount),
    laterYears: Math.round(later * 1000) / 1000,
    laterAmount: r2(laterAmount),
    full: r2(full),
    factor: Math.round(factor * 10000) / 10000,
    amount: r2(full * factor),
  };
}

export function eosbWage(e: HrEmployee | HrEmployeeInput, rules: HrRules): number {
  return rules.eosb.wage === "basic_housing" ? (e.basic || 0) + (e.housing || 0) : grossPay(e);
}

export const leaveDays = (years: number, rules: HrRules) =>
  years >= rules.leave.afterYears ? rules.leave.daysAfter : rules.leave.days;

export function isCosted(
  e: Pick<HrEmployee, "employment" | "outsourcedMonthly" | "basic" | "housing" | "transport" | "other">,
) {
  if (e.employment === "outsourced") return e.outsourcedMonthly != null && e.outsourcedMonthly > 0;
  return grossPay(e) > 0;
}

export interface HrCostLine {
  employeeId: string;
  employeeNo: string;
  name: string;
  department: string | null;
  trade: Trade | null;
  costCentre: string | null;
  saudi: boolean;
  employment: Employment;
  status: EmployeeStatus;
  principalId: string | null;
  serviceYears: number;
  costed: boolean;
  gross: number;
  gosiBase: number;
  gosiEmployer: number;
  gosiEmployee: number;
  gosiEmployerPct: number | null;
  gosiEmployeePct: number | null;
  gosiRegime: string | null;
  gosiRegimeSource: string | null;
  eosbAccrual: number;
  medical: number;
  expatFees: number;
  contract: number;
  monthlyCost: number;
  annualCost: number;
  leaveDays: number;
  dailyHours: number;
  productiveHours: number;
  hourlyRate: number | null;
  overtimeRate: number | null;
  eosbLiability: number;
}

/** One person's month, hour and liabilities. */
export function costLine(e: HrEmployee, rules: HrRules, on: Date): HrCostLine {
  const h = rules.hours;
  const years = serviceYears(e.hiredOn, on);
  const weekly = e.weeklyHours || h.weeklyMax;
  const daily = weekly / h.workdaysPerWeek;
  const saudi = isSaudi(e);
  const outsourced = e.employment === "outsourced";
  let gBase = 0,
    gEr = 0,
    gEe = 0,
    erPct: number | null = null,
    eePct: number | null = null,
    regime: string | null = null,
    regimeSource: string | null = null;
  let pay = grossPay(e);
  let eosbAccrual = 0,
    medical = 0,
    expat = 0,
    contract = 0,
    liability = 0,
    otRate: number | null = null,
    days: number,
    leave = 0;
  const yearDays = 52 * h.workdaysPerWeek;
  if (outsourced) {
    contract = e.outsourcedMonthly ?? 0;
    pay = 0;
    days = yearDays - h.publicHolidayDays;
  } else {
    const g = rules.gosi;
    const base = (e.basic || 0) + (e.housing || 0);
    gBase = base <= 0 ? 0 : Math.min(Math.max(base, g.baseMin), g.baseMax);
    if (saudi) {
      const ne = newEntrant(e, rules);
      const [aEr, aEe] = annuityRates(rules, ne.isNew, on);
      erPct = aEr + g.sanedEmployer + g.hazardsEmployer;
      eePct = aEe + g.sanedEmployee;
      regime = ne.isNew ? "new entrant" : "legacy";
      regimeSource = ne.source;
    } else {
      erPct = g.hazardsEmployer;
      eePct = 0;
      regime = "non-Saudi";
      regimeSource = "recorded";
    }
    gEr = (gBase * erPct) / 100;
    gEe = (gBase * eePct) / 100;
    const wage = eosbWage(e, rules);
    const r = rules.eosb;
    eosbAccrual = (wage * (years < r.firstYears ? r.firstRateMonths : r.laterRateMonths)) / 12;
    liability = eosb(wage, years, "termination", rules).full;
    medical = (e.medicalAnnual ?? rules.medicalAnnualDefault) / 12;
    if (!saudi) {
      const x = rules.expat;
      expat = x.levyMonthly + (x.iqamaAnnual + x.workPermitAnnual + x.exitReentryAnnual + x.ticketAnnual) / 12;
    }
    leave = leaveDays(years, rules);
    days = yearDays - leave - h.publicHolidayDays - h.sickAllowanceDays;
    if (pay > 0) otRate = pay / (30 * daily) + (rules.overtime.premiumOfBasic * (e.basic || 0)) / (30 * daily);
  }
  const ok = isCosted(e);
  const monthly = ok ? pay + gEr + eosbAccrual + medical + expat + contract : 0;
  const hours = Math.max(1, days * daily);
  return {
    employeeId: e.id,
    employeeNo: e.employeeNo,
    name: e.name,
    department: e.department,
    trade: e.trade,
    costCentre: e.costCentre,
    saudi,
    employment: e.employment,
    status: e.status,
    principalId: e.principalId,
    serviceYears: r2(years),
    costed: ok,
    gross: r2(pay),
    gosiBase: r2(gBase),
    gosiEmployer: r2(gEr),
    gosiEmployee: r2(gEe),
    gosiEmployerPct: erPct == null ? null : r2(erPct),
    gosiEmployeePct: eePct == null ? null : r2(eePct),
    gosiRegime: regime,
    gosiRegimeSource: regimeSource,
    eosbAccrual: r2(eosbAccrual),
    medical: r2(medical),
    expatFees: r2(expat),
    contract: r2(contract),
    monthlyCost: r2(monthly),
    annualCost: r2(monthly * 12),
    leaveDays: leave,
    dailyHours: r2(daily),
    productiveHours: r1(hours),
    hourlyRate: ok ? r2((monthly * 12) / hours) : null,
    overtimeRate: ok && otRate != null ? r2(otRate) : null,
    eosbLiability: ok ? r2(liability) : 0,
  };
}

export function lastMonths(on: Date, n = 12): string[] {
  const out: string[] = [];
  let y = on.getUTCFullYear(),
    m = on.getUTCMonth() + 1;
  for (let i = 0; i < n; i++) {
    out.push(`${y}-${String(m).padStart(2, "0")}`);
    m -= 1;
    if (m === 0) {
      y -= 1;
      m = 12;
    }
  }
  return out.reverse();
}

export interface HrGroup {
  key: string;
  heads: number;
  monthly: number;
  annual: number;
  hourlyRate: number | null;
}

function group(lines: HrCostLine[], key: "department" | "trade" | "costCentre"): HrGroup[] {
  const acc = new Map<string, { heads: number; monthly: number; annual: number; rates: number[] }>();
  for (const ln of lines) {
    const k = ln[key] || "—";
    const a = acc.get(k) ?? { heads: 0, monthly: 0, annual: 0, rates: [] };
    a.heads += 1;
    a.monthly += ln.monthlyCost;
    a.annual += ln.annualCost;
    if (ln.hourlyRate != null) a.rates.push(ln.hourlyRate);
    acc.set(k, a);
  }
  return [...acc.entries()]
    .map(([k, a]) => ({
      key: k,
      heads: a.heads,
      monthly: r2(a.monthly),
      annual: r2(a.annual),
      hourlyRate: a.rates.length ? r2(a.rates.reduce((s, v) => s + v, 0) / a.rates.length) : null,
    }))
    .sort((a, b) => b.annual - a.annual);
}

/** The mean hourly cost of the costed, current people in each trade. */
export function tradeRates(lines: HrCostLine[]): Map<string, number> {
  const acc = new Map<string, number[]>();
  for (const ln of lines)
    if (ln.hourlyRate != null && ln.trade && ln.status !== "left")
      acc.set(ln.trade, [...(acc.get(ln.trade) ?? []), ln.hourlyRate]);
  return new Map([...acc.entries()].map(([k, v]) => [k, r2(v.reduce((s, x) => s + x, 0) / v.length)]));
}

export interface HrOvertimeEntry {
  employeeId: string;
  month: string;
  hours: number;
}

export interface HrCost {
  asOf: string;
  currency: string;
  rulesBasis: "default" | "entered";
  lines: HrCostLine[];
  totals: {
    heads: number;
    direct: number;
    outsourced: number;
    saudi: number;
    gross: number;
    gosiEmployer: number;
    eosbAccrual: number;
    medical: number;
    expatFees: number;
    contract: number;
    monthly: number;
    annual: number;
    eosbLiability: number;
    overtime12m: number;
    overtimeHours12m: number;
  };
  byDepartment: HrGroup[];
  byTrade: HrGroup[];
  byCostCentre: HrGroup[];
  overtime: {
    months: { month: string; hours: number; cost: number }[];
    people: { employeeId: string; name: string; hours: number; cost: number }[];
  };
  coverage: { people: number; costed: number; uncosted: number; boundToUser: number; left: number };
  method: string;
}

export const COST_METHOD =
  "month = gross pay + GOSI employer share on basic+housing (floored and capped) + end-of-service accrual (half a month's wage a year for five years, a month's after) + medical premium ÷ 12 + for non-Saudis the monthly levy and a twelfth of iqama, permit, exit/re-entry and ticket; an outsourced head is its contract charge. Hour = year ÷ (working days − leave − public holidays − sick allowance) × daily hours. Overtime = hourly wage + 50% of hourly basic (art. 107).";

export function costSummary(employees: HrEmployee[], overtime: HrOvertimeEntry[], rules: HrRules, on: Date): HrCost {
  const current = employees.filter((e) => e.status !== "left");
  const lines = current.map((e) => costLine(e, rules, on));
  const byId = new Map(lines.map((l) => [l.employeeId, l]));
  const months = lastMonths(on, 12);
  const otMonths = new Map(months.map((m) => [m, { month: m, hours: 0, cost: 0 }]));
  const otPeople = new Map<string, { employeeId: string; name: string; hours: number; cost: number }>();
  for (const o of overtime) {
    const m = o.month.slice(0, 7);
    const ln = byId.get(o.employeeId);
    const slot = otMonths.get(m);
    if (!slot || !ln) continue;
    const rate = ln.overtimeRate ?? ln.hourlyRate ?? 0;
    const cost = o.hours * rate;
    slot.hours += o.hours;
    slot.cost += cost;
    const p = otPeople.get(ln.employeeId) ?? { employeeId: ln.employeeId, name: ln.name, hours: 0, cost: 0 };
    p.hours += o.hours;
    p.cost += cost;
    otPeople.set(ln.employeeId, p);
  }
  const tot = (k: keyof HrCostLine) => r2(lines.reduce((s, l) => s + ((l[k] as number) || 0), 0));
  const otList = [...otMonths.values()];
  return {
    asOf: on.toISOString().slice(0, 10),
    currency: rules.currency,
    rulesBasis: rules.basis,
    lines: [...lines].sort(
      (a, b) => (a.department ?? "").localeCompare(b.department ?? "") || a.name.localeCompare(b.name),
    ),
    totals: {
      heads: lines.length,
      direct: lines.filter((l) => l.employment !== "outsourced").length,
      outsourced: lines.filter((l) => l.employment === "outsourced").length,
      saudi: lines.filter((l) => l.saudi && l.employment !== "outsourced").length,
      gross: tot("gross"),
      gosiEmployer: tot("gosiEmployer"),
      eosbAccrual: tot("eosbAccrual"),
      medical: tot("medical"),
      expatFees: tot("expatFees"),
      contract: tot("contract"),
      monthly: tot("monthlyCost"),
      annual: tot("annualCost"),
      eosbLiability: tot("eosbLiability"),
      overtime12m: r2(otList.reduce((s, v) => s + v.cost, 0)),
      overtimeHours12m: r1(otList.reduce((s, v) => s + v.hours, 0)),
    },
    byDepartment: group(lines, "department"),
    byTrade: group(lines, "trade"),
    byCostCentre: group(lines, "costCentre"),
    overtime: {
      months: otList.map((v) => ({ month: v.month, hours: r1(v.hours), cost: r2(v.cost) })),
      people: [...otPeople.values()]
        .map((p) => ({ ...p, hours: r1(p.hours), cost: r2(p.cost) }))
        .sort((a, b) => b.cost - a.cost)
        .slice(0, 15),
    },
    coverage: {
      people: lines.length,
      costed: lines.filter((l) => l.costed).length,
      uncosted: lines.filter((l) => !l.costed).length,
      boundToUser: lines.filter((l) => l.principalId).length,
      left: employees.length - current.length,
    },
    method: COST_METHOD,
  };
}

/* ---- Saudization ---------------------------------------------------------------- */

export function nitaqatWeight(e: Pick<HrEmployee, "basic" | "housing">, rules: HrRules): number {
  const n = rules.nitaqat;
  const wage = (e.basic || 0) + (e.housing || 0);
  if (wage >= n.fullAtWage) return 1;
  if (n.halfAtWage != null && wage >= n.halfAtWage) return 0.5;
  return 0;
}

export function hiresNeeded(counted: number, expats: number, targetPct: number | null): number | null {
  if (targetPct == null) return null;
  const t = targetPct / 100;
  if (t >= 1) return expats ? null : 0;
  const need = (t * (counted + expats) - counted) / (1 - t);
  return Math.max(0, Math.ceil(need - 1e-9));
}

export interface HrShare {
  heads: number;
  saudis: number;
  counted: number;
  expats: number;
  pct: number | null;
}

export interface HrSaudization extends HrShare {
  asOf: string;
  rulesBasis: "default" | "entered";
  targetPct: number | null;
  gapPct: number | null;
  hiresNeeded: number | null;
  belowWage: { employeeId: string; name: string; department: string | null; wage: number; weight: number }[];
  byDepartment: (HrShare & { key: string })[];
  byNationality: { nationality: string; heads: number }[];
  outsourced: number;
  fullAtWage: number;
  halfAtWage: number | null;
  method: string;
}

export const SAUDIZATION_METHOD =
  "Nitaqat share = Saudis counted ÷ (Saudis counted + non-Saudis), direct employees only. A Saudi counts 1 at or above the full-count wage (basic + housing, as registered with GOSI) and ½ at or above the half-count wage. Outsourced heads count for their contractor. Hires needed = full-count Saudis to add to reach the target with no one leaving.";

export function saudization(employees: HrEmployee[], rules: HrRules, on: Date): HrSaudization {
  const direct = employees.filter((e) => e.status !== "left" && e.employment !== "outsourced");
  const target = rules.nitaqat.targetPct;
  const share = (g: HrEmployee[]): HrShare => {
    const saudis = g.filter(isSaudi);
    const counted = saudis.reduce((s, e) => s + nitaqatWeight(e, rules), 0);
    const expats = g.length - saudis.length;
    const den = counted + expats;
    return { heads: g.length, saudis: saudis.length, counted, expats, pct: den ? r1((counted / den) * 100) : null };
  };
  const whole = share(direct);
  const depts = new Map<string, HrEmployee[]>();
  const nat = new Map<string, number>();
  for (const e of direct) {
    const k = e.department || "—";
    depts.set(k, [...(depts.get(k) ?? []), e]);
    const n = (e.nationality || "—").toUpperCase();
    nat.set(n, (nat.get(n) ?? 0) + 1);
  }
  return {
    asOf: on.toISOString().slice(0, 10),
    rulesBasis: rules.basis,
    ...whole,
    targetPct: target,
    gapPct: target != null && whole.pct != null ? r1(target - whole.pct) : null,
    hiresNeeded: hiresNeeded(whole.counted, whole.expats, target),
    belowWage: direct
      .filter((e) => isSaudi(e) && nitaqatWeight(e, rules) < 1)
      .map((e) => ({
        employeeId: e.id,
        name: e.name,
        department: e.department,
        wage: r2((e.basic || 0) + (e.housing || 0)),
        weight: nitaqatWeight(e, rules),
      }))
      .sort((a, b) => a.wage - b.wage),
    byDepartment: [...depts.entries()].map(([key, g]) => ({ key, ...share(g) })).sort((a, b) => b.heads - a.heads),
    byNationality: [...nat.entries()]
      .map(([nationality, heads]) => ({ nationality, heads }))
      .sort((a, b) => b.heads - a.heads),
    outsourced: employees.filter((e) => e.status !== "left" && e.employment === "outsourced").length,
    fullAtWage: rules.nitaqat.fullAtWage,
    halfAtWage: rules.nitaqat.halfAtWage,
    method: SAUDIZATION_METHOD,
  };
}

/* ---- labour: the work, priced in people's time -------------------------------- */

export interface HrWorkRecord {
  id: string;
  number: string;
  title: string;
  kind: string;
  status: string;
  openedAt: string;
  completedAt: string | null;
  hours: number | null;
  personId: string | null;
  assetId: string | null;
  assetTag: string | null;
  assetType: string | null;
  assetTypeName: string | null;
  category: string | null;
}

export interface HrPlanRecord {
  id: string;
  title: string;
  assetId: string | null;
  assetTag: string | null;
  category: string | null;
  estimatedHours: number | null;
  intervalDays: number | null;
  active: boolean;
}

export interface HrLabourRow {
  id: string;
  number: string;
  title: string;
  kind: string;
  status: string;
  completedAt: string | null;
  openedAt: string;
  assetId: string | null;
  assetTag: string | null;
  assetTypeName: string | null;
  trade: string | null;
  person: string | null;
  hours: number;
  rate: number | null;
  rateSource: "person" | "trade" | null;
  cost: number | null;
}

export interface HrLabourAgg {
  key: string;
  name?: string;
  hours: number;
  cost: number;
  workOrders: number;
  rate?: number | null;
}

export interface HrLabour {
  asOf: string;
  months: number;
  /** the rules the trade rates were priced with; set by the router and the sample */
  rulesBasis?: "default" | "entered";
  coverage: {
    workOrders: number;
    withHours: number;
    byPerson: number;
    byTrade: number;
    uncostedWithHours: number;
    noHours: number;
  };
  totals: { hours: number; cost: number };
  byTrade: HrLabourAgg[];
  byAssetType: HrLabourAgg[];
  topAssets: {
    assetId: string;
    tag: string | null;
    typeName: string | null;
    hours: number;
    cost: number;
    workOrders: number;
  }[];
  byMonth: { month: string; hours: number; cost: number }[];
  workOrders: HrLabourRow[];
  plans: {
    annualHours: number;
    annualCost: number;
    costed: number;
    uncosted: number;
    rows: {
      id: string;
      title: string;
      assetTag: string | null;
      assetId: string | null;
      trade: string | null;
      estimatedHours: number | null;
      perYear: number | null;
      hoursPerYear: number | null;
      rate: number | null;
      costPerYear: number | null;
    }[];
  };
  tradeRates: { trade: string; rate: number; heads: number }[];
  method: string;
}

export const LABOUR_METHOD =
  "cost = labour hours × the hourly cost of the person who did the work, when the work order names a platform user bound to an employee; otherwise × the mean hourly cost of the trade that works on the asset's category. Work with no hours, or no person and no asset trade, is counted, not costed. Plans: estimated hours × (365 ÷ interval days) × the trade's hourly cost.";

export function labour(
  work: HrWorkRecord[],
  plans: HrPlanRecord[],
  lines: HrCostLine[],
  on: Date,
  months = 12,
  rules?: Pick<HrRules, "tradeByCategory"> | null,
): HrLabour {
  const byPerson = new Map(
    lines.filter((l) => l.principalId && l.hourlyRate != null).map((l) => [l.principalId as string, l]),
  );
  const rates = tradeRates(lines);
  const heads = new Map<string, number>();
  for (const l of lines) if (l.trade && l.costed) heads.set(l.trade, (heads.get(l.trade) ?? 0) + 1);

  const monthAcc = new Map(lastMonths(on, 12).map((m) => [m, { month: m, hours: 0, cost: 0 }]));
  const tradeAcc = new Map<string, HrLabourAgg>();
  const typeAcc = new Map<string, HrLabourAgg>();
  const assetAcc = new Map<string, HrLabour["topAssets"][number]>();
  const rows: HrLabourRow[] = [];
  const cov = { workOrders: work.length, withHours: 0, byPerson: 0, byTrade: 0, uncostedWithHours: 0, noHours: 0 };
  let hoursTotal = 0,
    costTotal = 0;
  const bump = (acc: Map<string, HrLabourAgg>, key: string, hours: number, cost: number, name?: string) => {
    const a = acc.get(key) ?? { key, ...(name !== undefined ? { name } : {}), hours: 0, cost: 0, workOrders: 0 };
    a.hours += hours;
    a.cost += cost;
    a.workOrders += 1;
    acc.set(key, a);
  };
  for (const w of work) {
    let trade: string | null = tradeOfCategory(w.category, rules);
    const person = w.personId ? byPerson.get(w.personId) : undefined;
    let rate: number | null = null;
    let source: "person" | "trade" | null = null;
    if (person) {
      rate = person.hourlyRate;
      source = "person";
      trade = person.trade ?? trade;
    } else if (trade && rates.has(trade)) {
      rate = rates.get(trade) as number;
      source = "trade";
    }
    if (w.hours == null || w.hours <= 0) {
      cov.noHours += 1;
      continue;
    }
    const hours = w.hours;
    cov.withHours += 1;
    const cost = rate != null ? hours * rate : null;
    if (source === "person") cov.byPerson += 1;
    else if (source === "trade") cov.byTrade += 1;
    else cov.uncostedWithHours += 1;
    hoursTotal += hours;
    rows.push({
      id: w.id,
      number: w.number,
      title: w.title,
      kind: w.kind,
      status: w.status,
      completedAt: w.completedAt,
      openedAt: w.openedAt,
      assetId: w.assetId,
      assetTag: w.assetTag,
      assetTypeName: w.assetTypeName,
      trade,
      person: person?.name ?? null,
      hours: r2(hours),
      rate,
      rateSource: source,
      cost: cost == null ? null : r2(cost),
    });
    if (cost == null) continue;
    costTotal += cost;
    const when = (w.completedAt ?? w.openedAt).slice(0, 7);
    const slot = monthAcc.get(when);
    if (slot) {
      slot.hours += hours;
      slot.cost += cost;
    }
    bump(tradeAcc, trade ?? "—", hours, cost);
    bump(typeAcc, w.assetType ?? "—", hours, cost, w.assetTypeName ?? "—");
    if (w.assetId) {
      const a = assetAcc.get(w.assetId) ?? {
        assetId: w.assetId,
        tag: w.assetTag,
        typeName: w.assetTypeName,
        hours: 0,
        cost: 0,
        workOrders: 0,
      };
      a.hours += hours;
      a.cost += cost;
      a.workOrders += 1;
      assetAcc.set(w.assetId, a);
    }
  }
  const fin = <T extends { hours: number; cost: number }>(items: Iterable<T>): T[] =>
    [...items].map((a) => ({ ...a, hours: r1(a.hours), cost: r2(a.cost) })).sort((a, b) => b.cost - a.cost);

  const planRows: HrLabour["plans"]["rows"] = [];
  let pHours = 0,
    pCost = 0,
    pUncosted = 0;
  for (const p of plans) {
    if (!p.active) continue;
    const trade = tradeOfCategory(p.category, rules);
    const perYear = p.intervalDays ? 365 / p.intervalDays : null;
    const yearly = p.estimatedHours != null && perYear ? p.estimatedHours * perYear : null;
    const rate = trade ? (rates.get(trade) ?? null) : null;
    const cost = yearly != null && rate != null ? yearly * rate : null;
    if (cost == null) pUncosted += 1;
    else {
      pHours += yearly as number;
      pCost += cost;
    }
    planRows.push({
      id: p.id,
      title: p.title,
      assetTag: p.assetTag,
      assetId: p.assetId,
      trade,
      estimatedHours: p.estimatedHours,
      perYear: perYear == null ? null : r2(perYear),
      hoursPerYear: yearly == null ? null : r1(yearly),
      rate,
      costPerYear: cost == null ? null : r2(cost),
    });
  }
  const byTrade = fin(tradeAcc.values()).map((t) => ({ ...t, rate: rates.get(t.key) ?? null }));
  return {
    asOf: on.toISOString().slice(0, 10),
    months,
    coverage: cov,
    totals: { hours: r1(hoursTotal), cost: r2(costTotal) },
    byTrade,
    byAssetType: fin(typeAcc.values()),
    topAssets: fin(assetAcc.values()).slice(0, 15),
    byMonth: [...monthAcc.values()].map((v) => ({ month: v.month, hours: r1(v.hours), cost: r2(v.cost) })),
    workOrders: rows
      .sort((a, b) => (b.completedAt ?? b.openedAt).localeCompare(a.completedAt ?? a.openedAt))
      .slice(0, 200),
    plans: {
      annualHours: r1(pHours),
      annualCost: r2(pCost),
      costed: planRows.length - pUncosted,
      uncosted: pUncosted,
      rows: planRows.sort((a, b) => (b.costPerYear ?? 0) - (a.costPerYear ?? 0)).slice(0, 200),
    },
    tradeRates: [...rates.entries()]
      .map(([trade, rate]) => ({ trade, rate, heads: heads.get(trade) ?? 0 }))
      .sort((a, b) => a.trade.localeCompare(b.trade)),
    method: LABOUR_METHOD,
  };
}

/** A person's figures for the register, the way the platform adds them to each row. */
export function describeEmployee(
  e: Omit<HrEmployee, "saudi" | "serviceYears" | "gosiRegime" | "gosiRegimeSource">,
  rules: HrRules,
  on: Date,
): HrEmployee {
  const saudi = isSaudi(e);
  const ne = newEntrant(e, rules);
  return {
    ...e,
    saudi,
    serviceYears: r2(serviceYears(e.hiredOn, on)),
    gosiRegime: saudi ? (ne.isNew ? "new entrant" : "legacy") : null,
    gosiRegimeSource: saudi ? ne.source : null,
  };
}
